import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { Combobox, type ComboboxOption } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { getClients } from "../../../../lib/clients";
import { IconBtn } from "../../../ui";
import { NewClientModal } from "../../_shared/NewClientModal";

/**
 * The size of a CRM form field, worn by the Combobox trigger.
 *
 * The shared `Combobox` is kept because the CRM has no searchable one-of-many
 * field of its own, and a document has to be able to reach a client that is
 * three hundred rows down. Its chrome (pill radius, sunken fill, no border) now
 * comes from the `[data-slot="combobox-trigger"]` rule in `index.css`, so all
 * that is left here is the measurement that makes it the same 52px field as
 * everything else on the form, plus the two flatteners `crm-inp` implies and
 * the shared rule leaves to the caller.
 */
const PILL = "h-[52px] border-0 px-[18px] text-[13.5px] shadow-none";

/**
 * Picks the client a sales document belongs to, by id.
 *
 * Shared by the document editor and the expenses form. Unlike the picker in
 * `_shared/`, this one can also CREATE the client it cannot find: writing an
 * invoice for somebody who is not in the CRM yet is the normal case, and
 * sending the operator off to the clients page loses the half-typed document.
 */
export function ClientPicker({
  value,
  onChange,
  disabled,
}: {
  value?: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [showNew, setShowNew] = useState(false);

  const loadClients = useCallback(() => {
    getClients({ pageSize: 100 }).then((res) =>
      setOptions(res.rows.map((c) => ({ value: c.id, label: c.name, hint: c.regCode ?? undefined }))),
    );
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  return (
    <>
      <div className="flex items-center gap-2.5">
        <div className="min-w-0 flex-1">
          <Combobox
            options={options}
            value={value}
            onChange={onChange}
            disabled={disabled}
            className={PILL}
            popoverClassName="rounded-2xl"
            placeholder={t("sales.pickClient")}
            searchPlaceholder={t("sales.searchClient")}
            emptyLabel={t("sales.noClients")}
          />
        </div>
        {!disabled ? (
          <IconBtn title={t("clients.add")} aria-label={t("clients.add")} onClick={() => setShowNew(true)}>
            <Plus className="size-4" />
          </IconBtn>
        ) : null}
      </div>

      {showNew ? (
        <NewClientModal
          onClose={() => setShowNew(false)}
          onCreate={(c) => {
            // Straight into the list rather than a reload: the new client is
            // selected immediately below, and a refetch would race with that.
            setOptions((prev) => [{ value: c.id, label: c.name, hint: c.regCode ?? undefined }, ...prev]);
            onChange(c.id);
          }}
        />
      ) : null}
    </>
  );
}
